import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text, Button, ScrollView } from '@bonanza-social/moonlight';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { useUserDetails } from '../../hooks';
import { AttestationSummaryProps } from './AttestationSummary.types';
import { useTranslation } from '@bonanza-social/i18n';
import { filterObjectByKeys } from '../../utils';
import {
  reassessAccount,
  selectReassessmentDataState,
} from '../../state/detailsAttestation.slice';

function AttestationSummary({
  openAttestationSection,
  onClose,
}: AttestationSummaryProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('detailsAttestation');
  const { userDetails } = useUserDetails();
  const reassessmentDataState = useSelector(selectReassessmentDataState);
  const [filteredDetails, setFilteredDetails] = useState<
    Record<string, string>
  >({});

  const neededKeys = useMemo(
    () => [
      'firstName',
      'lastName',
      'addressLine1',
      'occupation',
      'phoneNumber',
    ],
    [],
  );

  const filteredData = useMemo(() => {
    if (!userDetails) {
      return {};
    }
    const filteredData = filterObjectByKeys(neededKeys, userDetails);
    if ('phoneNumber' in filteredData && filteredData.phoneNumber) {
      const phoneCode = userDetails.phoneCode
        ? `${userDetails.phoneCode}-`
        : '';
      const phoneNumber = filteredData.phoneNumber;
      filteredData.phoneNumber = `${phoneCode}${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6)}`;
    }
    if ('addressLine1' in filteredData && filteredData.addressLine1) {
      filteredData.addressLine1 = `${userDetails.addressLine1} ${userDetails.city}, ${userDetails.province}, ${userDetails.postalCode}`;
    }
    return filteredData;
  }, [userDetails, neededKeys]);

  useEffect(() => {
    if (userDetails) {
      setFilteredDetails(filteredData);
    }
  }, [userDetails, filteredData]);

  const handleLooksGoodButton = useCallback(async () => {
    const result = await dispatch(reassessAccount());

    if (reassessAccount.fulfilled.match(result)) {
      requestAnimationFrame(() => {
        onClose();
      });
    }
  }, [dispatch, onClose]);

  const renderedDetails = useMemo(() => {
    return Object.entries(filteredDetails).map(([key, value]) => (
      <Box
        key={key}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        paddingVertical="space-500"
        borderBottomWidth="border-width-025"
        borderColor="borderPrimary"
      >
        <Text
          locKey={key}
          locNS="detailsAttestation"
          variant="body-s-semi"
          color="darkSurfaceSecondary"
          flex={1}
        />
        <Text
          variant="body-s-semi"
          flex={2}
          textAlign="right"
          color="darkSurfacePrimary"
        >
          {value}
        </Text>
      </Box>
    ));
  }, [filteredDetails]);

  return (
    <Box flex={1} gap="space-500">
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
        <Box>
          <Text
            variant="h4"
            color="darkSurfacePrimary"
            locNS="detailsAttestation"
            locKey="personalDetailsConfirmation"
            marginBottom="space-400"
            paddingTop="space-300"
            marginTop="safe-area-inset-top"
          />
        </Box>
        <Text
          variant="body-m-semi"
          locKey="attestationExplanation"
          locNS="detailsAttestation"
          marginBottom="space-400"
          color="darkSurfacePrimary"
        ></Text>
        <Box borderRadius="radius-300" overflow="hidden">
          {renderedDetails}
          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            marginTop="space-400"
          >
            <Text
              locKey="pepHeader"
              locNS="detailsAttestation"
              variant="body-s-semi"
              color="darkSurfaceSecondary"
              flex={1}
            ></Text>
            <Text
              variant="body-s-semi"
              flex={2}
              textAlign="right"
              color="darkSurfacePrimary"
            >
              {t('pepLabel')}
            </Text>
          </Box>
        </Box>
      </ScrollView>
      <Box flexDirection="row" gap="space-200" paddingBottom="space-800">
        <Button
          variant="secondary"
          locKey="updateDetails"
          locNS="detailsAttestation"
          onPress={() => openAttestationSection(true)}
          leftIcon="edit"
          flex={1}
          size="large"
        ></Button>
        <Button
          variant="primary"
          locKey="looksGood"
          locNS="detailsAttestation"
          onPress={handleLooksGoodButton}
          loading={reassessmentDataState === 'pending'}
          leftIcon="check"
          flex={1}
          size="large"
        ></Button>
      </Box>
    </Box>
  );
}

export default memo(AttestationSummary);
